import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import EsitoBadge from "../../components/EsitoBadge";
import { ComuneSelect } from "../../components/ComuneSelect";
import { toast } from "sonner";

const empty = {
  titolo: "", tipologia: "Appartamento", indirizzo: "", comune: "Vicenza", zona: "",
  provincia: "Vicenza",
  catasto_foglio: "", catasto_particella: "", catasto_subalterno: "", catasto_categoria: "",
  catasto_pertinenze: [],
  superficie: 0, locali: 0, camere: null, bagni: null,
  piano: "", classe_energetica: "", anno: null, prezzo: 0,
  cantina: false, box_auto: false, terrazzo: false, balcone: false,
  riscaldamento: "", ascensore: false, stato_manutentivo: "", arredato: "",
  spese_condominiali_annue: null,
  descrizione: "", highlights: [],
  agency_id: "", agente_nome: "", agente_phone: "", agente_email: "",
  seller_id: "",
  data_ingresso_galleria: "", stato_pubblicazione: "", data_vendita: "",
  esito: null, verifiche: [],
};

const RESPONSO_OPTIONS = [
  { v: "", l: "— non attribuito —" },
  { v: "in_ordine", l: "✓ In ordine" },
  { v: "criticita_dichiarata", l: "» Criticità dichiarata" },
  { v: "vizio_trasferibilita", l: "✗ Vizio sulla trasferibilità" },
  { v: "non_applicabile", l: "– Non applicabile" },
];

const CLASSI_ENERGETICHE = ["A4", "A3", "A2", "A1", "B", "C", "D", "E", "F", "G"];
const RISCALDAMENTI = ["Centralizzato", "Autonomo a metano", "Autonomo in pompa di calore", "Gasolio", "GPL"];
const CATEGORIE_PRINCIPALI = [
  { v: "A/1",  l: "A/1 — Abitazioni di tipo signorile" },
  { v: "A/2",  l: "A/2 — Abitazioni di tipo civile" },
  { v: "A/3",  l: "A/3 — Abitazioni di tipo economico" },
  { v: "A/4",  l: "A/4 — Abitazioni di tipo popolare" },
  { v: "A/5",  l: "A/5 — Abitazioni di tipo ultrapopolare" },
  { v: "A/6",  l: "A/6 — Abitazioni di tipo rurale" },
  { v: "A/7",  l: "A/7 — Abitazioni in villini" },
  { v: "A/8",  l: "A/8 — Abitazioni in ville" },
  { v: "A/9",  l: "A/9 — Castelli, palazzi di pregio" },
  { v: "A/10", l: "A/10 — Uffici e studi privati" },
  { v: "A/11", l: "A/11 — Alloggi tipici (rifugi, baite)" },
];
const CATEGORIE_PERTINENZE = [
  { v: "C/2", l: "C/2 — Magazzini/locali di deposito (cantine, soffitte)" },
  { v: "C/6", l: "C/6 — Stalle/rimesse/autorimesse (box, posti auto)" },
  { v: "C/7", l: "C/7 — Tettoie chiuse od aperte" },
];
const CATEGORIE_TUTTE = [...CATEGORIE_PRINCIPALI, ...CATEGORIE_PERTINENZE];

const formatDateIT = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

export default function PropertiesAdmin() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [editing, setEditing] = useState(null);

  // Client-side search & filters (combinabili AND)
  const [q, setQ] = useState("");
  const [fProvincia, setFProvincia] = useState("");
  const [fComune, setFComune] = useState("");
  const [fAgenzia, setFAgenzia] = useState("");
  const [fEsito, setFEsito] = useState("");

  const agencyFilter = params.get("agency") || "";
  const statoFilter = params.get("stato") || "";
  const esitoFilter = params.get("esito") || "";
  const publishedFilter = params.get("published") || "";
  const agencyName = agencies.find((a) => a.id === agencyFilter)?.name;
  const urlFilterActive = !!(agencyFilter || statoFilter || esitoFilter || publishedFilter);

  const load = () => {
    const q = new URLSearchParams();
    if (params.get("esito")) q.set("esito", params.get("esito"));
    if (params.get("published")) q.set("published", params.get("published"));
    if (agencyFilter) q.set("agency_id", agencyFilter);
    if (statoFilter) q.set("stato", statoFilter);
    api.get("/properties?" + q.toString()).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); api.get("/agencies").then((r) => setAgencies(r.data)); api.get("/sellers").then((r) => setSellers(r.data)); }, [params]);

  // Deep-link handler: ?edit=<id>&focus=<test-id-prefix> opens the modal auto-focused
  useEffect(() => {
    const editId = params.get("edit");
    if (!editId || editing) return;
    const target = items.find((p) => p.id === editId);
    if (target) {
      setEditing({ ...target });
      const focus = params.get("focus");
      if (focus) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="${focus}"]`);
          if (el) {
            el.scrollIntoView({ block: "center", behavior: "smooth" });
            try { el.focus(); } catch {}
            el.classList.add("ring-2", "ring-[#7D2E3F]");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#7D2E3F]"), 2500);
          }
        }, 400);
      }
      // Clean up URL so it does not re-trigger
      const np = new URLSearchParams(params);
      np.delete("edit"); np.delete("focus");
      setParams(np, { replace: true });
    }
  }, [items, params, editing, setParams]);

  const setStatoTab = (v) => {
    const np = new URLSearchParams(params);
    if (v) np.set("stato", v); else np.delete("stato");
    setParams(np, { replace: true });
  };
  const clearAgencyFilter = () => {
    const np = new URLSearchParams(params);
    np.delete("agency");
    setParams(np, { replace: true });
  };

  const save = async () => {
    try {
      const body = { ...editing };
      const esitoWanted = editing.esito ?? null;
      const verificheWanted = editing.verifiche || [];
      // Rimuove campi non appartenenti a PropertyIn (potenzialmente aggiunti dalla list augmentata o dal DB)
      ["fascicolo_code","fascicolo_code_at","id","photos","verifiche","documents","riuc",
       "created_at","updated_at","published","published_at","esito","esito_at","esito_by",
       "agency","seller","prerender_status","prerender_generated_at","prerender_updated_at",
       "dati_catastali"].forEach((k) => delete body[k]);
      ["superficie", "locali", "prezzo"].forEach((k) => body[k] = Number(body[k]) || 0);
      ["camere","bagni","spese_condominiali_annue"].forEach((k) => body[k] = body[k] === "" || body[k] == null ? null : Number(body[k]));
      if (body.anno) body.anno = Number(body.anno); else body.anno = null;
      if (!body.agency_id) body.agency_id = null;
      if (!body.seller_id) body.seller_id = null;
      if (!body.provincia) body.provincia = "Vicenza";
      body.highlights = (body.highlights || []).filter(h => (h || "").trim());
      ["data_ingresso_galleria", "stato_pubblicazione", "data_vendita"].forEach((k) => {
        if (body[k] === "" || body[k] == null) body[k] = null;
      });
      const pid = editing.id;
      if (pid) await api.put(`/properties/${pid}`, body);
      else {
        const r = await api.post("/properties", body);
        // Nuovi immobili non hanno esito/verifiche, i campi sono ignorati
        setEditing(null); load(); toast.success("Salvato"); return;
      }
      // Aree: patch solo per campi effettivamente modificati rispetto all'originale.
      // Nota: l'ordine conta — se questa modifica introduce un'area Rilevante,
      // il backend farà auto-downgrade a "criticita" da "pronto". Applichiamo prima le
      // aree, poi l'esito, così il vincolo di coerenza server-side vede lo stato finale.
      const original = items.find((x) => x.id === pid);
      const origVerifiche = (original?.verifiche || []).reduce((acc, v) => { acc[v.key] = v; return acc; }, {});
      const AREA_FIELDS = ["status", "responso", "rilevante", "note", "criticita", "costo_stimato", "tempi", "a_carico_di", "tecnico_id"];
      let autoDowngraded = false;
      for (const v of verificheWanted) {
        const orig = origVerifiche[v.key] || {};
        const patch = {};
        for (const k of AREA_FIELDS) {
          const nv = v[k] ?? null;
          const ov = orig[k] ?? null;
          if (nv !== ov) patch[k] = nv;
        }
        if (Object.keys(patch).length > 0) {
          const r = await api.patch(`/properties/${pid}/verifica/${v.key}`, patch);
          if (r.data?.esito_auto_downgrade) autoDowngraded = true;
        }
      }
      // Esito globale: aggiorna se cambiato. Il backend rifiuta "pronto" incoerente (400).
      if ((original?.esito ?? null) !== esitoWanted) {
        if (esitoWanted) {
          try { await api.post(`/properties/${pid}/esito`, { esito: esitoWanted }); }
          catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Esito non applicabile");
            load(); return;
          }
        }
      }
      setEditing(null); load();
      toast.success(autoDowngraded
        ? "Salvato — esito riclassificato automaticamente a \"Pronto con Criticità Dichiarate\" (area Rilevante)"
        : "Salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Carica verifiche dell'immobile quando si apre in modifica (dal DB, non dalla lista)
  useEffect(() => {
    if (editing?.id && (!editing.verifiche || editing.verifiche.length === 0)) {
      api.get(`/properties/${editing.id}`).then((r) => {
        setEditing((prev) => prev && prev.id === r.data.id
          ? { ...prev, verifiche: r.data.verifiche || [], esito: r.data.esito ?? null }
          : prev);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  const updateArea = (key, patch) => {
    setEditing((prev) => ({
      ...prev,
      verifiche: (prev.verifiche || []).map((v) => {
        if (v.key !== key) return v;
        const merged = { ...v, ...patch };
        // Coerenza UI (specchia il backend): se Responso non è "criticita_dichiarata",
        // Classificazione Criticità e testo pubblico vanno azzerati, non solo disabilitati.
        if ((merged.responso || "") !== "criticita_dichiarata") {
          merged.rilevante = null;
          merged.criticita = "";
        }
        return merged;
      }),
    }));
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/properties/${id}`); load(); };
  const publish = async (id, published) => {
    try { await api.post(`/properties/${id}/${published ? "unpublish" : "publish"}`); load(); toast.success(published ? "Rimosso dalla Galleria" : "Pubblicato in Galleria"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile pubblicare", { duration: 6000 }); }
  };
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const regenAllStale = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/properties/prerender/regenerate-stale");
      toast.success(`Rigenerate ${r.data.regenerated}/${r.data.considered} anteprime`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rigenerazione bulk non riuscita");
    } finally {
      setBulkBusy(false);
    }
  };

  // Derived filter options + filtered items
  const provinceOptions = React.useMemo(
    () => Array.from(new Set(items.map((p) => p.provincia).filter(Boolean))).sort(),
    [items]
  );
  const comuneOptions = React.useMemo(() => {
    const src = fProvincia ? items.filter((p) => p.provincia === fProvincia) : items;
    return Array.from(new Set(src.map((p) => p.comune).filter(Boolean))).sort();
  }, [items, fProvincia]);
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((p) => {
      if (needle) {
        const sellerName = p.seller ? `${p.seller.nome || ""} ${p.seller.cognome || ""}` : "";
        const hay = [p.fascicolo_code, p.titolo, p.comune, p.indirizzo, sellerName]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (fProvincia && p.provincia !== fProvincia) return false;
      if (fComune && p.comune !== fComune) return false;
      if (fAgenzia && p.agency_id !== fAgenzia) return false;
      if (fEsito && p.esito !== fEsito) return false;
      return true;
    });
  }, [items, q, fProvincia, fComune, fAgenzia, fEsito]);
  const anyFilterActive = !!(q || fProvincia || fComune || fAgenzia || fEsito || urlFilterActive);
  const staleCount = React.useMemo(
    () => items.filter((p) => (p.prerender_status || "never") !== "updated").length,
    [items]
  );
  const resetFilters = () => {
    setQ(""); setFProvincia(""); setFComune(""); setFAgenzia(""); setFEsito("");
    if (urlFilterActive) {
      const np = new URLSearchParams(params);
      ["agency","stato","esito","published"].forEach((k) => np.delete(k));
      setParams(np, { replace: true });
    }
  };

  return (
    <div data-testid="properties-admin">
      <div className="flex justify-between items-end mb-8 gap-3 flex-wrap">
        <div><div className="label-uppercase">Fascicoli</div><h1 className="font-serif text-4xl mt-2">Immobili</h1></div>
        <div className="flex items-center gap-3">
          <button
            data-testid="regen-stale-btn"
            onClick={regenAllStale}
            disabled={bulkBusy || staleCount === 0}
            className={`text-xs label-uppercase px-3 py-2 border ${
              bulkBusy || staleCount === 0
                ? "border-[#E6E4E0] text-[#B8B4AD] cursor-not-allowed"
                : "border-[#91590F] text-[#91590F] hover:bg-[#FDF6EA]"
            }`}
            title={staleCount === 0 ? "Tutte le anteprime sono aggiornate" : `Rigenera ${staleCount} anteprime`}
          >
            {bulkBusy ? "Rigenerazione…" : `Rigenera anteprime${staleCount ? ` (${staleCount})` : ""}`}
          </button>
          <button data-testid="new-property-btn" onClick={() => setEditing({ ...empty })} className="btn-primary">Nuovo immobile</button>
        </div>
      </div>

      {/* Filtri: stato pubblicazione + eventuale agenzia */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="label-uppercase text-[#5C5657]">Stato:</span>
        {[
          { v: "", l: "Tutti" },
          { v: "in_vendita", l: "In vendita" },
          { v: "venduto", l: "Venduto" },
        ].map((t) => (
          <button
            key={t.v || "all"}
            data-testid={`filter-stato-${t.v || "all"}`}
            onClick={() => setStatoTab(t.v)}
            className={`text-xs label-uppercase px-3 py-1 border ${
              statoFilter === t.v
                ? "border-[#7D2E3F] text-[#7D2E3F] bg-[#FDFDFC]"
                : "border-[#E6E4E0] text-[#5C5657] hover:text-[#3A3D42]"
            }`}
          >
            {t.l}
          </button>
        ))}
        {agencyFilter && (
          <div className="ml-auto flex items-center gap-2 text-xs" data-testid="agency-filter-banner">
            <span className="label-uppercase text-[#5C5657]">Agenzia:</span>
            <span className="px-2 py-1 border border-[#7D2E3F] text-[#7D2E3F]">{agencyName || agencyFilter}</span>
            <button onClick={clearAgencyFilter} className="text-[#B3261E] label-uppercase" data-testid="clear-agency-filter">× Rimuovi</button>
          </div>
        )}
      </div>

      {urlFilterActive && (
        <div className="mb-3 flex items-center gap-2 border border-[#91590F] bg-[#FDF6EA] text-[#7A4A0C] px-3 py-2 text-xs" data-testid="url-filter-banner">
          <strong>Filtro rapido attivo:</strong>
          {esitoFilter && <span className="px-2 py-1 border border-[#91590F]">Esito: {esitoFilter}</span>}
          {publishedFilter && <span className="px-2 py-1 border border-[#91590F]">In Galleria: {publishedFilter}</span>}
          {statoFilter && <span className="px-2 py-1 border border-[#91590F]">Stato: {statoFilter}</span>}
          {agencyFilter && <span className="px-2 py-1 border border-[#91590F]">Agenzia: {agencyName || agencyFilter}</span>}
          <button
            onClick={resetFilters}
            className="ml-auto label-uppercase text-[#B3261E]"
            data-testid="clear-url-filters"
          >× Mostra tutti</button>
        </div>
      )}

      {/* Barra ricerca + filtri combinabili (AND) */}
      <div className="mb-4 border border-[#E6E4E0] bg-white p-3 flex flex-wrap items-center gap-2" data-testid="filters-bar">
        <input
          data-testid="filter-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca codice Fascicolo, titolo, comune, indirizzo, venditore…"
          className="admin-input flex-1 min-w-[240px] max-w-md"
        />
        <select data-testid="filter-provincia" value={fProvincia} onChange={(e) => { setFProvincia(e.target.value); setFComune(""); }} className="admin-input w-40">
          <option value="">Provincia (tutte)</option>
          {provinceOptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="filter-comune" value={fComune} onChange={(e) => setFComune(e.target.value)} className="admin-input w-40">
          <option value="">Comune (tutti)</option>
          {comuneOptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="filter-agenzia" value={fAgenzia} onChange={(e) => setFAgenzia(e.target.value)} className="admin-input w-52">
          <option value="">Agenzia (tutte)</option>
          {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select data-testid="filter-esito" value={fEsito} onChange={(e) => setFEsito(e.target.value)} className="admin-input w-56">
          <option value="">Esito (tutti)</option>
          <option value="pronto">Pronto alla Vendita</option>
          <option value="criticita">Con criticità dichiarate</option>
          <option value="non_conforme">Non conforme</option>
        </select>
        <button
          data-testid="filter-reset"
          onClick={resetFilters}
          disabled={!anyFilterActive}
          className={`text-xs label-uppercase px-3 py-2 border ${anyFilterActive ? "border-[#7D2E3F] text-[#7D2E3F] hover:bg-[#FDFDFC]" : "border-[#E6E4E0] text-[#B8B4AD] cursor-not-allowed"}`}
        >
          Reset filtri
        </button>
        <div className="ml-auto text-xs text-[#5C5657]" data-testid="filter-count">
          {filtered.length} / {items.length}
        </div>
      </div>

      <table className="admin-table w-full bg-white border border-[#E6E4E0]">
        <thead><tr><th>Titolo</th><th>Fascicolo</th><th>Comune</th><th>Agenzia</th><th>Esito</th><th>In Galleria</th><th title="Stato anteprima social">Anteprima</th><th>Ingresso</th><th>Stato</th><th></th></tr></thead>
        <tbody>
          {filtered.map((p) => {
            const ps = p.prerender_status || "never";
            const dotCls = ps === "updated" ? "bg-[#245A2E]" : ps === "stale" ? "bg-[#91590F]" : "bg-[#B8B4AD]";
            const dotTitle = ps === "updated" ? "Anteprima aggiornata" : ps === "stale" ? "Anteprima da rigenerare" : "Anteprima mai generata";
            return (
            <tr key={p.id} data-testid={`prop-row-${p.id}`}>
              <td><Link to={`/admin/immobili/${p.id}`} className="font-medium text-[#7D2E3F] hover:underline">{p.titolo}</Link>
                  <div className="text-xs text-[#5C5657]">{p.tipologia} · {p.superficie}m² · € {p.prezzo?.toLocaleString("it-IT")}</div></td>
              <td className="font-mono text-xs whitespace-nowrap text-[#7D2E3F]" data-testid={`prop-code-${p.id}`}>{p.fascicolo_code || "—"}</td>
              <td>{p.comune}</td>
              <td className="text-xs">{p.agency?.name || "—"}</td>
              <td><EsitoBadge esito={p.esito} /></td>
              <td>{p.published ? <span className="badge-stamp badge-pronto">Online</span> : <span className="text-xs text-[#5C5657]">—</span>}</td>
              <td>
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full ${dotCls}`}
                  title={dotTitle}
                  data-testid={`prop-prerender-${p.id}`}
                  data-status={ps}
                />
              </td>
              <td className="text-xs whitespace-nowrap" data-testid={`prop-ingresso-${p.id}`}>{formatDateIT(p.data_ingresso_galleria)}</td>
              <td className="text-xs whitespace-nowrap" data-testid={`prop-stato-${p.id}`}>
                {p.stato_pubblicazione === "venduto" ? (
                  <span className="badge-stamp badge-non_conforme">Venduto</span>
                ) : p.stato_pubblicazione === "in_vendita" ? (
                  <span className="badge-stamp badge-pronto">In vendita</span>
                ) : (
                  <span className="text-[#5C5657]">—</span>
                )}
              </td>
              <td className="text-right space-x-3 whitespace-nowrap">
                {(p.esito === "pronto" || p.esito === "criticita") && (
                  <button data-testid={`toggle-publish-${p.id}`} onClick={() => publish(p.id, p.published)} className="text-xs label-uppercase text-[#7D2E3F]">
                    {p.published ? "Rimuovi" : "Pubblica"}
                  </button>
                )}
                <button
                  data-testid={`prop-edit-${p.id}`}
                  onClick={() => setEditing(p)}
                  className="text-xs label-uppercase text-[#7D2E3F]"
                >
                  Modifica
                </button>
                <Link to={`/admin/immobili/${p.id}`} className="text-xs label-uppercase text-[#7D2E3F]">Fascicolo</Link>
                <button onClick={() => del(p.id)} className="text-xs label-uppercase text-[#B3261E]">Elimina</button>
              </td>
            </tr>
            );
          })}
          {filtered.length === 0 && <tr><td colSpan="10" className="text-center py-8 text-[#5C5657]">{anyFilterActive ? "Nessun immobile corrisponde ai filtri" : "Nessun immobile"}</td></tr>}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E6E4E0] max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto" data-testid="property-modal-body">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#F2EBEB] sticky top-[-32px] -mt-2 pt-2 bg-white z-10">
              <h2 className="font-serif text-2xl">{editing.id ? "Modifica" : "Nuovo"} immobile</h2>
              <div className="text-xs text-[#5C5657] italic">Scorri per vedere tutti i campi ↓</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 bg-[#F9F5F5] border border-[#E6E4E0] px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="label-uppercase text-[#7D2E3F]">Codice Fascicolo</div>
                  <div className="text-xs text-[#5C5657] mt-0.5">Assegnato automaticamente dal sistema — non modificabile</div>
                </div>
                <div className="font-mono text-sm tracking-wider text-[#7D2E3F]" data-testid="prop-fascicolo-code">
                  {editing.fascicolo_code || "verrà assegnato al salvataggio"}
                </div>
              </div>
              <div className="col-span-2"><label className="label-uppercase">Titolo *</label><input data-testid="prop-titolo" value={editing.titolo} onChange={(e) => setEditing({ ...editing, titolo: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Tipologia</label>
                <select value={editing.tipologia} onChange={(e) => setEditing({ ...editing, tipologia: e.target.value })} className="admin-input">
                  {["Appartamento","Villa","Casa singola","Bifamiliare","Rustico"].map((t) => <option key={t}>{t}</option>)}
                </select></div>
              <div><label className="label-uppercase">Indirizzo *</label><input value={editing.indirizzo} onChange={(e) => setEditing({ ...editing, indirizzo: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Provincia *</label>
                <select
                  data-testid="prop-provincia"
                  value={editing.provincia || "Vicenza"}
                  onChange={(e) => setEditing({ ...editing, provincia: e.target.value, comune: "" })}
                  className="admin-input"
                >
                  {["Vicenza","Padova","Verona","Treviso","Venezia","Rovigo","Belluno"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div><label className="label-uppercase">Comune *</label>
                <ComuneSelect
                  value={editing.comune}
                  onChange={(v) => setEditing({ ...editing, comune: v })}
                  provincia={editing.provincia || null}
                  className="admin-input"
                  testid="prop-comune"
                />
              </div>
              <div><label className="label-uppercase">Zona</label><input value={editing.zona || ""} onChange={(e) => setEditing({ ...editing, zona: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Superficie (m²) *</label><input type="number" value={editing.superficie} onChange={(e) => setEditing({ ...editing, superficie: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Locali *</label>
                <select data-testid="prop-locali" value={editing.locali ?? ""} onChange={(e) => setEditing({ ...editing, locali: e.target.value === "" ? "" : Number(e.target.value) })} className="admin-input">
                  <option value="">— seleziona —</option>
                  <option value="1">1</option><option value="2">2</option><option value="3">3</option>
                  <option value="4">4</option><option value="5">5</option><option value="6">6 o più</option>
                </select>
              </div>
              <div><label className="label-uppercase">Piano</label>
                <select data-testid="prop-piano" value={editing.piano || ""} onChange={(e) => setEditing({ ...editing, piano: e.target.value })} className="admin-input">
                  <option value="">— seleziona —</option>
                  {["Seminterrato","Piano terra","Rialzato","1°","2°","3°","4°","5° o superiore","Attico","Ultimo piano"].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div><label className="label-uppercase">Classe energetica</label>
                <select value={editing.classe_energetica || ""} onChange={(e) => setEditing({ ...editing, classe_energetica: e.target.value })} className="admin-input">
                  <option value="">— seleziona —</option>
                  {CLASSI_ENERGETICHE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="label-uppercase">Anno</label><input type="number" value={editing.anno || ""} onChange={(e) => setEditing({ ...editing, anno: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Prezzo (€) *</label><input type="number" value={editing.prezzo} onChange={(e) => setEditing({ ...editing, prezzo: e.target.value })} className="admin-input" /></div>
              <div className="col-span-2"><label className="label-uppercase">Descrizione</label><textarea rows={3} value={editing.descrizione || ""} onChange={(e) => setEditing({ ...editing, descrizione: e.target.value })} className="admin-input" /></div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4 mt-2">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Composizione</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="label-uppercase">Camere</label>
                    <select data-testid="prop-camere" value={editing.camere ?? ""} onChange={(e) => setEditing({ ...editing, camere: e.target.value === "" ? null : Number(e.target.value) })} className="admin-input">
                      <option value="">— seleziona —</option>
                      <option value="1">1</option><option value="2">2</option><option value="3">3</option>
                      <option value="4">4</option><option value="5">5 o più</option>
                    </select>
                  </div>
                  <div><label className="label-uppercase">Bagni</label>
                    <select data-testid="prop-bagni" value={editing.bagni ?? ""} onChange={(e) => setEditing({ ...editing, bagni: e.target.value === "" ? null : Number(e.target.value) })} className="admin-input">
                      <option value="">— seleziona —</option>
                      <option value="1">1</option><option value="2">2</option><option value="3">3</option>
                      <option value="4">4 o più</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Pertinenze</div>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  {[{k:"cantina",l:"Cantina"},{k:"box_auto",l:"Box auto"},{k:"terrazzo",l:"Terrazzo"},{k:"balcone",l:"Balcone"}].map(({k,l})=>(
                    <label key={k} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!editing[k]} onChange={(e)=>setEditing({...editing,[k]:e.target.checked})} />{l}
                    </label>
                  ))}
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Tecniche</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="label-uppercase">Riscaldamento</label>
                    <select value={editing.riscaldamento || ""} onChange={(e)=>setEditing({...editing, riscaldamento: e.target.value})} className="admin-input">
                      <option value="">— seleziona —</option>
                      {RISCALDAMENTI.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div><label className="label-uppercase">Ascensore</label>
                    <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer"><input type="checkbox" checked={!!editing.ascensore} onChange={(e)=>setEditing({...editing, ascensore: e.target.checked})} /> Presente</label></div>
                  <div><label className="label-uppercase">Stato manutentivo</label>
                    <select value={editing.stato_manutentivo || ""} onChange={(e)=>setEditing({...editing, stato_manutentivo: e.target.value})} className="admin-input">
                      <option value="">—</option><option value="nuovo">Nuovo / recente</option><option value="buono">Buono stato</option><option value="da_ristrutturare">Da ristrutturare</option>
                    </select></div>
                  <div><label className="label-uppercase">Arredato</label>
                    <select value={editing.arredato || ""} onChange={(e)=>setEditing({...editing, arredato: e.target.value})} className="admin-input">
                      <option value="">—</option><option value="si">Sì</option><option value="no">No</option><option value="parziale">Parziale</option>
                    </select></div>
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Economici</div>
                <div><label className="label-uppercase">Spese condominiali annue (€)</label><input type="number" min="0" value={editing.spese_condominiali_annue ?? ""} onChange={(e)=>setEditing({...editing, spese_condominiali_annue: e.target.value})} className="admin-input" /></div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4" data-testid="prop-catasto">
                <div className="flex justify-between items-center mb-3">
                  <div className="label-uppercase text-[#7D2E3F]">Catasto (strutturato)</div>
                  <button
                    type="button"
                    data-testid="add-pertinenza"
                    onClick={() => setEditing({ ...editing, catasto_pertinenze: [...(editing.catasto_pertinenze || []), { tipo: "Garage", foglio: "", particella: "", subalterno: "", categoria: "" }] })}
                    className="text-xs label-uppercase text-[#7D2E3F]"
                  >
                    + Aggiungi pertinenza
                  </button>
                </div>

                {/* Immobile principale */}
                <div className="mb-4">
                  <div className="text-xs text-[#5C5657] mb-1">Immobile principale</div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2"><label className="label-uppercase">Tipo</label>
                      <div className="admin-input bg-[#FBFAF7] text-[#5C5657]">Abitazione</div>
                    </div>
                    <div className="col-span-2"><label className="label-uppercase">Foglio</label><input value={editing.catasto_foglio || ""} onChange={(e) => setEditing({ ...editing, catasto_foglio: e.target.value })} className="admin-input" /></div>
                    <div className="col-span-3"><label className="label-uppercase">Particella</label><input value={editing.catasto_particella || ""} onChange={(e) => setEditing({ ...editing, catasto_particella: e.target.value })} className="admin-input" /></div>
                    <div className="col-span-2"><label className="label-uppercase">Sub.</label><input value={editing.catasto_subalterno || ""} onChange={(e) => setEditing({ ...editing, catasto_subalterno: e.target.value })} className="admin-input" /></div>
                    <div className="col-span-2"><label className="label-uppercase">Categoria</label>
                      <select value={editing.catasto_categoria || ""} onChange={(e) => setEditing({ ...editing, catasto_categoria: e.target.value })} className="admin-input">
                        <option value="">—</option>
                        {CATEGORIE_PRINCIPALI.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                      </select>
                    </div>
                    <div className="col-span-1"></div>
                  </div>
                </div>

                {/* Pertinenze */}
                {(editing.catasto_pertinenze || []).length === 0 ? (
                  <div className="text-xs text-[#8A8484] py-2">Nessuna pertinenza aggiunta. Usa <span className="text-[#7D2E3F]">+ Aggiungi pertinenza</span> per inserire garage, posti auto, cantine, soffitte.</div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-xs text-[#5C5657]">Pertinenze ({editing.catasto_pertinenze.length})</div>
                    {editing.catasto_pertinenze.map((p, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2" data-testid={`pertinenza-${i}`}>
                        <div className="col-span-2"><label className="label-uppercase">Tipo</label>
                          <select
                            value={p.tipo || "Garage"}
                            onChange={(e) => { const a = [...editing.catasto_pertinenze]; a[i] = { ...a[i], tipo: e.target.value }; setEditing({ ...editing, catasto_pertinenze: a }); }}
                            className="admin-input"
                          >
                            <option value="Garage">Garage</option>
                            <option value="Posto auto">Posto auto</option>
                            <option value="Cantina">Cantina</option>
                            <option value="Soffitta">Soffitta</option>
                            <option value="Locale accessorio">Locale accessorio</option>
                            <option value="Altro">Altro</option>
                          </select>
                        </div>
                        <div className="col-span-2"><label className="label-uppercase">Foglio</label><input value={p.foglio || ""} onChange={(e) => { const a = [...editing.catasto_pertinenze]; a[i] = { ...a[i], foglio: e.target.value }; setEditing({ ...editing, catasto_pertinenze: a }); }} className="admin-input" /></div>
                        <div className="col-span-3"><label className="label-uppercase">Particella</label><input value={p.particella || ""} onChange={(e) => { const a = [...editing.catasto_pertinenze]; a[i] = { ...a[i], particella: e.target.value }; setEditing({ ...editing, catasto_pertinenze: a }); }} className="admin-input" /></div>
                        <div className="col-span-2"><label className="label-uppercase">Sub.</label><input value={p.subalterno || ""} onChange={(e) => { const a = [...editing.catasto_pertinenze]; a[i] = { ...a[i], subalterno: e.target.value }; setEditing({ ...editing, catasto_pertinenze: a }); }} className="admin-input" /></div>
                        <div className="col-span-2"><label className="label-uppercase">Categoria</label>
                          <select value={p.categoria || ""} onChange={(e) => { const a = [...editing.catasto_pertinenze]; a[i] = { ...a[i], categoria: e.target.value }; setEditing({ ...editing, catasto_pertinenze: a }); }} className="admin-input">
                            <option value="">—</option>
                            {CATEGORIE_TUTTE.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                          </select>
                        </div>
                        <div className="col-span-1 flex items-end justify-center">
                          <button
                            type="button"
                            data-testid={`rm-pertinenza-${i}`}
                            onClick={() => setEditing({ ...editing, catasto_pertinenze: editing.catasto_pertinenze.filter((_, idx) => idx !== i) })}
                            className="text-[#B3261E] pb-2 text-lg"
                            title="Rimuovi pertinenza"
                          >×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="flex justify-between items-center mb-3">
                  <div className="label-uppercase text-[#7D2E3F]">Highlight editoriali (3-5 punti forti)</div>
                  <button type="button" onClick={()=>setEditing({...editing, highlights:[...(editing.highlights||[]), ""]})} className="text-xs label-uppercase text-[#7D2E3F]">+ Aggiungi punto</button>
                </div>
                <div className="space-y-2">
                  {(editing.highlights||[]).map((h,i)=>(
                    <div key={i} className="flex gap-2">
                      <input value={h} onChange={(e)=>{const a=[...editing.highlights];a[i]=e.target.value;setEditing({...editing, highlights:a});}} className="admin-input" placeholder="Es. Vista sui Colli Berici" />
                      <button type="button" onClick={()=>setEditing({...editing, highlights: editing.highlights.filter((_,idx)=>idx!==i)})} className="text-[#B3261E] px-2">×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Pubblicazione in Galleria</div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label-uppercase">Data ingresso</label>
                    <input
                      type="date"
                      data-testid="prop-data-ingresso"
                      value={(editing.data_ingresso_galleria || "").slice(0, 10)}
                      onChange={(e) => setEditing({ ...editing, data_ingresso_galleria: e.target.value })}
                      className="admin-input"
                    />
                    <div className="text-xs text-[#5C5657] mt-1">Auto-compilata alla prima pubblicazione</div>
                  </div>
                  <div>
                    <label className="label-uppercase">Stato</label>
                    <select
                      data-testid="prop-stato-pubblicazione"
                      value={editing.stato_pubblicazione || ""}
                      onChange={(e) => setEditing({ ...editing, stato_pubblicazione: e.target.value })}
                      className="admin-input"
                    >
                      <option value="">—</option>
                      <option value="in_vendita">In vendita</option>
                      <option value="venduto">Venduto</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-uppercase">Data vendita</label>
                    <input
                      type="date"
                      data-testid="prop-data-vendita"
                      value={(editing.data_vendita || "").slice(0, 10)}
                      onChange={(e) => setEditing({ ...editing, data_vendita: e.target.value })}
                      className="admin-input"
                      disabled={editing.stato_pubblicazione !== "venduto"}
                    />
                    <div className="text-xs text-[#5C5657] mt-1">Auto-compilata al passaggio a Venduto</div>
                  </div>
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Venditore / Proprietario</div>
                <select
                  data-testid="prop-seller"
                  value={editing.seller_id || ""}
                  onChange={(e) => setEditing({ ...editing, seller_id: e.target.value })}
                  className="admin-input"
                >
                  <option value="">— nessuno collegato —</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.cognome} {s.nome}{s.codice_fiscale ? ` · ${s.codice_fiscale}` : ""}{s.consenso_pubblicazione ? "" : " · SENZA CONSENSO"}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-[#5C5657] mt-2">
                  Il venditore va gestito nella sezione <Link to="/admin/venditori" className="text-[#7D2E3F] underline">Venditori</Link>. Senza venditore o senza consenso, la pubblicazione in Galleria è bloccata.
                </div>
              </div>

              <div className="col-span-2 border-t border-[#E6E4E0] pt-4">
                <div className="label-uppercase text-[#7D2E3F] mb-3">Agenzia e Agente</div>
              </div>
              <div className="col-span-2"><label className="label-uppercase">Agenzia</label>
                <select value={editing.agency_id || ""} onChange={(e) => {
                  const aid = e.target.value;
                  setEditing({ ...editing, agency_id: aid, agente_nome: "", agente_phone: "", agente_email: "" });
                }} className="admin-input" data-testid="prop-agency">
                  <option value="">— nessuna —</option>
                  {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select></div>

              {editing.agency_id && (() => {
                const ag = agencies.find(a => a.id === editing.agency_id);
                const agents = (ag?.agents || []).filter(x => x?.name);
                const currentIdx = agents.findIndex(x => x.name === editing.agente_nome);
                const isCustom = editing.agente_nome && currentIdx < 0;
                return (
                  <>
                    <div className="col-span-2"><label className="label-uppercase">Agente di riferimento</label>
                      {agents.length > 0 ? (
                        <select
                          data-testid="prop-agent-select"
                          value={currentIdx >= 0 ? String(currentIdx) : (isCustom ? "custom" : "")}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "") setEditing({ ...editing, agente_nome: "", agente_phone: "", agente_email: "" });
                            else if (v === "custom") setEditing({ ...editing, agente_nome: "", agente_phone: "", agente_email: "" });
                            else {
                              const a = agents[Number(v)];
                              setEditing({ ...editing, agente_nome: a.name || "", agente_phone: a.phone || "", agente_email: a.email || "" });
                            }
                          }}
                          className="admin-input"
                        >
                          <option value="">— nessuno —</option>
                          {agents.map((a, i) => (
                            <option key={i} value={String(i)}>{a.name}</option>
                          ))}
                          <option value="custom">Altro (inserisci manualmente)</option>
                        </select>
                      ) : (
                        <div className="text-xs text-[#91590F] py-2">L'anagrafica di questa agenzia non ha ancora agenti. Aggiungili dalla scheda agenzia o compila manualmente sotto.</div>
                      )}
                    </div>
                    {(isCustom || agents.length === 0) && (
                      <div className="col-span-2"><label className="label-uppercase">Nome agente (personalizzato)</label>
                        <input value={editing.agente_nome || ""} onChange={(e) => setEditing({ ...editing, agente_nome: e.target.value })} className="admin-input" placeholder="Nome e cognome" />
                      </div>
                    )}
                  </>
                );
              })()}

              <div><label className="label-uppercase">Agente — Tel</label><input value={editing.agente_phone || ""} onChange={(e) => setEditing({ ...editing, agente_phone: e.target.value })} className="admin-input" /></div>
              <div><label className="label-uppercase">Agente — Email</label><input value={editing.agente_email || ""} onChange={(e) => setEditing({ ...editing, agente_email: e.target.value })} className="admin-input" /></div>

              {/* Esito Globale del Protocollo (solo se immobile esistente) */}
              {editing.id && (() => {
                const hasRilevanti = (editing.verifiche || []).some((v) => v.rilevante === true);
                return (
                <div className="col-span-2 border-t border-[#E6E4E0] pt-4" data-testid="edit-esito-block">
                  <div className="label-uppercase text-[#7D2E3F] mb-3">Esito globale del Protocollo</div>
                  <div className="flex gap-3 flex-wrap">
                    {[
                      { v: "pronto", l: "Pronto alla Vendita", cls: "badge-pronto" },
                      { v: "criticita", l: "Pronto alla Vendita con Criticità Dichiarate", cls: "badge-criticita" },
                      { v: "non_conforme", l: "Non Conforme", cls: "badge-non_conforme" },
                    ].map((o) => {
                      const sel = editing.esito === o.v;
                      const disabled = o.v === "pronto" && hasRilevanti;
                      const title = disabled
                        ? "Non selezionabile: il Fascicolo contiene almeno un'area classificata come Rilevante. L'unico esito ammesso è \"Pronto alla Vendita con Criticità Dichiarate\"."
                        : sel ? "Clicca di nuovo per rimuovere l'esito" : `Attribuisci esito: ${o.l}`;
                      return (
                        <button
                          key={o.v}
                          type="button"
                          data-testid={`edit-esito-${o.v}`}
                          disabled={disabled}
                          title={title}
                          onClick={() => setEditing({ ...editing, esito: sel ? null : o.v })}
                          className={`badge-stamp ${o.cls} ${sel ? "ring-2 ring-offset-2 ring-[#7D2E3F]" : "opacity-60 hover:opacity-100"} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
                        >
                          {sel ? "● " : ""}{o.l}
                        </button>
                      );
                    })}
                  </div>
                  {hasRilevanti && (
                    <div className="text-xs text-[#91590F] mt-3 border border-[#91590F] bg-[#FDF6EA] px-3 py-2" data-testid="esito-coherence-warning">
                      Il Fascicolo contiene <strong>{(editing.verifiche || []).filter(v => v.rilevante === true).length}</strong> area/e con Classificazione Criticità <strong>Rilevante</strong>. L&apos;unico esito coerente è &quot;Pronto alla Vendita con Criticità Dichiarate&quot;. Il vincolo è applicato anche dal backend.
                    </div>
                  )}
                  <div className="text-xs text-[#5C5657] mt-2">Clicca due volte sullo stesso bottone per rimuovere l&apos;esito. La modifica è applicata al Salva.</div>
                </div>
              );})()}

              {/* 10 Aree del Fascicolo (solo se immobile esistente, verifiche caricate) */}
              {editing.id && (editing.verifiche || []).length > 0 && (
                <div className="col-span-2 border-t border-[#E6E4E0] pt-4" data-testid="edit-verifiche-block">
                  <div className="label-uppercase text-[#7D2E3F] mb-3">10 Aree del Fascicolo</div>
                  <div className="space-y-3">
                    {editing.verifiche.map((v, idx) => (
                      <details key={v.key} className="border border-[#E6E4E0] bg-[#FBFAF7]" data-testid={`edit-area-${v.key}`}>
                        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between gap-3">
                          <span><span className="label-uppercase text-[#8A8580] mr-2">Area {String(idx + 1).padStart(2, "0")}</span><strong className="text-sm">{v.label}</strong></span>
                          <span className="text-[10px] label-uppercase text-[#5C5657]">
                            {v.responso === "in_ordine" ? "✓" : v.responso === "criticita_dichiarata" ? "»" : v.responso === "vizio_trasferibilita" ? "✗" : v.responso === "non_applicabile" ? "–" : "○"}
                          </span>
                        </summary>
                        <div className="px-4 pb-4 pt-2 space-y-3 bg-white border-t border-[#E6E4E0]">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="label-uppercase">Stato</label>
                              <select value={v.status || "da_fare"} onChange={(e) => updateArea(v.key, { status: e.target.value })} data-testid={`edit-status-${v.key}`} className="admin-input">
                                <option value="da_fare">Da fare</option>
                                <option value="in_corso">In corso</option>
                                <option value="completata">Completata</option>
                              </select>
                            </div>
                            <div>
                              <label className="label-uppercase">Responso</label>
                              <select value={v.responso || ""} onChange={(e) => updateArea(v.key, { responso: e.target.value || null })} data-testid={`edit-responso-${v.key}`} className="admin-input">
                                {RESPONSO_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="label-uppercase">Classificazione criticità</label>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" checked={v.rilevante === true}
                                       onChange={() => updateArea(v.key, { rilevante: true })}
                                       data-testid={`edit-rilevante-yes-${v.key}`}
                                       disabled={v.responso !== "criticita_dichiarata"} /> Rilevante
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" checked={v.rilevante === false}
                                       onChange={() => updateArea(v.key, { rilevante: false })}
                                       data-testid={`edit-rilevante-no-${v.key}`}
                                       disabled={v.responso !== "criticita_dichiarata"} /> Non Rilevante
                              </label>
                              {v.responso !== "criticita_dichiarata" && (
                                <span className="text-xs text-[#8A8484]">(attiva solo con &quot;Criticità dichiarata&quot;)</span>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div><label className="label-uppercase">Note interne</label>
                              <textarea rows={2} value={v.note || ""} onChange={(e) => updateArea(v.key, { note: e.target.value })} data-testid={`edit-note-${v.key}`} className="admin-input" /></div>
                            <div><label className="label-uppercase">Criticità dichiarata (pubblica)</label>
                              <textarea rows={2} value={v.criticita || ""} onChange={(e) => updateArea(v.key, { criticita: e.target.value })} data-testid={`edit-criticita-${v.key}`} className="admin-input" /></div>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div><label className="label-uppercase">Costo stimato (€)</label>
                              <input type="number" value={v.costo_stimato ?? ""} onChange={(e) => updateArea(v.key, { costo_stimato: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`edit-costo-${v.key}`} className="admin-input" /></div>
                            <div><label className="label-uppercase">Tempi</label>
                              <input value={v.tempi || ""} onChange={(e) => updateArea(v.key, { tempi: e.target.value })} data-testid={`edit-tempi-${v.key}`} className="admin-input" /></div>
                            <div><label className="label-uppercase">A carico di</label>
                              <input value={v.a_carico_di || ""} onChange={(e) => updateArea(v.key, { a_carico_di: e.target.value })} data-testid={`edit-carico-${v.key}`} className="admin-input" /></div>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                  <div className="text-xs text-[#5C5657] mt-3">
                    Foto, documenti area, tecnico assegnato e RIUC restano gestibili dalla scheda Fascicolo (upload) e verranno riportati in questo modal in una prossima iterazione. Tutte le altre modifiche sono applicate al Salva.
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[#E6E4E0] sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="btn-secondary">Annulla</button>
              <button data-testid="prop-save" onClick={save} className="btn-primary">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
